/**
 * Drop-in accordion tracking for fe-blocks pattern accordions.
 *
 * POC: on init, stamps tracking props onto each trigger as data attributes
 * (data-track, data-event, data-module, data-position, data-element-text).
 * On click, reads those attributes into the dataLayer push.
 *
 * Only stamps/tracks triggers inside [data-feb="<pattern-slug>"] when that
 * slug is listed in TRACKED_PATTERNS.
 *
 * Current FAQ payload: { event, module, position, element_text }.
 *
 * Helpers below (getModule, getAccordionTitle, getAccordionState, etc.) are
 * retained for future non-FAQ patterns that may need heading-based module
 * names, title slugs, or open/closed state.
 *
 * Module resolution (for future use):
 * 1. Closest preceding parent heading text (slugified)
 * 2. Parent container aria-label / data-title / data-name
 * 3. Fallback: accordions
 * FAQ-like headings normalize to faq_accordions.
 */
const DEFAULT_MODULE = 'accordions';
/** Pattern slug → { event, module } stamped onto triggers. */
const TRACKED_PATTERNS = {
    'section-faqs': { event: 'faq_expand', module: 'faq' },
};
const PATTERN_MARKER_SELECTOR = '[data-feb]';
const TRIGGER_SELECTOR = '.feb__accordion-trigger';
const HEADING_SELECTOR = 'h1, h2, h3, h4';

const getTrackedConfig = (trigger) => {
    const patternEl = trigger.closest(PATTERN_MARKER_SELECTOR);
    if (!patternEl) {
        return null;
    }
    return TRACKED_PATTERNS[patternEl.getAttribute('data-feb')] || null;
};

const getPosition = (trigger) => {
    const accordion = trigger.closest('.feb__accordion');
    const triggers = accordion ? [...accordion.querySelectorAll(TRIGGER_SELECTOR)] : [];
    const index = triggers.indexOf(trigger) + 1;
    return index > 0 ? String(index) : '1';
};

const getElementText = (trigger) => {
    const titleEl = trigger.querySelector('.feb__accordion-title');
    return (titleEl?.textContent || trigger.textContent || '').trim();
};

/** Retained for future patterns: `${modulePrefix}_${position}` (e.g. faq_1). */
const getAccordionTitle = (trigger, module) => {
    const prefix = module.replace(/_?accordions$/, '') || 'accordion';
    return `${prefix}_${getPosition(trigger)}`;
};

/** Retained for future patterns: capture-phase aria-expanded pre-click state. */
const getAccordionState = (trigger) => {
    return trigger.getAttribute('aria-expanded') === 'true' ? 'closed' : 'opened';
};

const slugify = (text) =>
    text
        .toLowerCase()
        .replace(/['']/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64);

const normalizeModule = (text) => {
    const slug = slugify(text);
    if (!slug) {
        return DEFAULT_MODULE;
    }
    if (/(^|_)faq(_|$)/.test(slug) || slug.includes('frequently_asked')) {
        return 'faq_accordions';
    }
    return slug;
};

const headingTextFromNode = (node) => {
    if (!node) {
        return '';
    }
    if (/^H[1-4]$/.test(node.tagName)) {
        return node.textContent?.trim() || '';
    }
    const nested = node.querySelector(HEADING_SELECTOR);
    return nested?.textContent?.trim() || '';
};

/** Retained for future patterns: resolve module from nearby heading / descriptors. */
const getModule = (trigger) => {
    const accordion = trigger.closest('.feb__accordion');
    if (!accordion) {
        return DEFAULT_MODULE;
    }

    let node = accordion;
    while (node && node !== document.body) {
        let sibling = node.previousElementSibling;
        while (sibling) {
            const headingText = headingTextFromNode(sibling);
            if (headingText) {
                return normalizeModule(headingText);
            }
            sibling = sibling.previousElementSibling;
        }

        const parent = node.parentElement;
        if (!parent || parent === document.body) {
            break;
        }

        const descriptor =
            parent.getAttribute('aria-label') ||
            parent.dataset?.title ||
            parent.dataset?.name;
        if (descriptor?.trim()) {
            return normalizeModule(descriptor.trim());
        }

        node = parent;
    }

    return DEFAULT_MODULE;
};

const stampTrackingAttributes = () => {
    document.querySelectorAll(PATTERN_MARKER_SELECTOR).forEach((patternEl) => {
        const config = TRACKED_PATTERNS[patternEl.getAttribute('data-feb')];
        if (!config) {
            return;
        }

        patternEl.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => {
            trigger.setAttribute('data-track', 'true');
            trigger.setAttribute('data-event', config.event);
            trigger.setAttribute('data-module', config.module);
            trigger.setAttribute('data-position', getPosition(trigger));
            trigger.setAttribute('data-element-text', getElementText(trigger));
        });
    });
};

const trackAccordionExpand = () => {
    stampTrackingAttributes();

    document.addEventListener(
        'click',
        (e) => {
            const trigger = e.target.closest(TRIGGER_SELECTOR);
            if (!trigger || trigger.getAttribute('data-track') !== 'true') {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: trigger.dataset.event,
                module: trigger.dataset.module,
                position: trigger.dataset.position,
                element_text: trigger.dataset.elementText,
            });
        },
        true
    );
};

export default trackAccordionExpand;
